import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import ConfirmDialog from './ConfirmDialog';
import YouTubeEmbed from './YouTubeEmbed';
import UploadedVideoPlayer from './UploadedVideoPlayer';
import FeedbackForm, { FeedbackItem } from './FeedbackForm';
import SpeechReport from './SpeechReport';
import CoachingReport from './CoachingReport';
import LockedSpeechCTA from './LockedSpeechCTA';
import { Collapsible } from './reportPrimitives';
import Larry from './Larry';
import MemberPicker from './MemberPicker';
import {
  getSpeech,
  getSpeechFeedback,
  submitFeedback,
  updateFeedback,
  deleteFeedback,
  getCurrentMember,
  getMeeting,
  getMembers,
  toggleSpeechVisibility,
  updateSpeech,
  uploadSpeechAudio,
  analyzeSpeechFromUrl,
  clearSpeechAudio,
  regenerateSpeechFeedback,
  requestFriend,
} from '../services/api';
import { showToast } from '../hooks/useToast';
import useBackgroundTranscription, { transcriptionTimeoutMs } from '../hooks/useBackgroundTranscription';
import { probeDuration } from '../utils/media';

export default function ClubSpeechDetail() {
  const { speechId } = useParams();
  const [speech, setSpeech] = useState(null);
  usePageTitle(speech?.presentation_title || 'Speech');
  const [feedback, setFeedback] = useState([]);
  const [aiFeedback, setAiFeedback] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 'none' | 'sent' | 'pending' | 'error' — drives the locked panel's CTA.
  const [friendRequestState, setFriendRequestState] = useState('none');
  const [meetingDate, setMeetingDate] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadPercent, setAudioUploadPercent] = useState(0);
  const [audioUploadError, setAudioUploadError] = useState(null);
  const [audioUploadSuccess, setAudioUploadSuccess] = useState(false);
  const [clearingAudio, setClearingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [audioUrlUploading, setAudioUrlUploading] = useState(false);
  const [audioUrlError, setAudioUrlError] = useState(null);
  const [pendingPublic, setPendingPublic] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [transcribingFileSize, setTranscribingFileSize] = useState(0);
  const [confirmClearAudio, setConfirmClearAudio] = useState(false);
  const prevSpeechIdRef = useRef(null);

  useEffect(() => {
    if (prevSpeechIdRef.current === speechId) return;
    prevSpeechIdRef.current = speechId;
    setPendingPublic(false);
    setTranscribingFileSize(0);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speechId]);

  // Drive the background-transcription banner. The out-of-band worker advances the
  // speech through status processing → transcribing → completed/failed; poll until it
  // reaches a terminal state. `status` is the authoritative signal; fall back to
  // transcript presence for legacy rows written before the status column existed.
  const status = speech?.status;
  // Uploaded videos (has_media) ride the same worker. 'uploading' = bytes never landed.
  const isTranscribing = !!((speech?.audio_file_id || speech?.has_media) && status !== 'completed' && status !== 'failed' && status !== 'uploading' && !speech?.transcript);
  const { elapsedSeconds: bgElapsed, progressPercent: bgPercent, errorMsg: bgError } = useBackgroundTranscription({
    enabled: isTranscribing,
    intervalMs: 15000,
    pollFn: () => getSpeech(speechId),
    isComplete: (res) => res?.speech?.status === 'completed' || !!(res?.speech?.transcript || res?.speech?.metrics || res?.speech?.metrics_json),
    isFailed: (res) => res?.speech?.status === 'failed',
    getError: () => 'Transcription failed. Please try re-uploading the recording.',
    onComplete: (res) => {
      setSpeech(res.speech);
      setAiFeedback(res.speech.ai_feedback || null);
      showToast('Analysis complete');
    },
    onFail: (msg) => showToast(msg || 'Transcription failed'),
    estimatedFileSize: transcribingFileSize,
    // Scale the poll timeout with estimated audio length (50 KB/s heuristic).
    timeoutMs: transcriptionTimeoutMs(transcribingFileSize / (50 * 1024)),
  });

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [speechRes, memberRes] = await Promise.all([
        getSpeech(speechId),
        getCurrentMember().catch(() => null),
      ]);
      if (!speechRes?.speech) throw new Error('Speech not found');
      setSpeech(speechRes.speech);
      if (memberRes?.member) setMember(memberRes.member);

      const currentMember = memberRes?.member;

      if (currentMember) {
        try {
          const feedbackRes = await getSpeechFeedback(speechId);
          if (feedbackRes?.feedback) setFeedback(feedbackRes.feedback);
          if (feedbackRes?.ai_feedback) setAiFeedback(feedbackRes.ai_feedback);
        } catch (fbErr) {
          console.warn('[SpeechDetail] Could not load feedback:', fbErr);
        }
      }

      if (currentMember?.role === 'admin' && speechRes.speech.meeting_id) {
        try {
          const meetingRes = await getMeeting(speechRes.speech.meeting_id);
          if (meetingRes?.meeting) {
            setMeetingDate(meetingRes.meeting.meeting_date);
          }
        } catch {
          // non-fatal
        }
      }
    } catch (err) {
      console.error('[SpeechDetail] Load error:', err);
      if (err.code === 'FORBIDDEN') {
        setError('You do not have permission to view this speech.');
      } else {
        setError(err.message || 'Failed to load speech');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async (content) => {
    await submitFeedback(speechId, content);
    const res = await getSpeechFeedback(speechId);
    if (res?.feedback) setFeedback(res.feedback);
  };

  const handleEditFeedback = async (feedbackId, content) => {
    await updateFeedback(feedbackId, content);
    const res = await getSpeechFeedback(speechId);
    if (res?.feedback) setFeedback(res.feedback);
  };

  const handleDeleteFeedback = async (feedbackId) => {
    await deleteFeedback(feedbackId);
    const res = await getSpeechFeedback(speechId);
    if (res?.feedback) setFeedback(res.feedback);
  };

  const [actionError, setActionError] = useState(null);
  const handleToggleVisibility = async () => {
    if (!speech) return;
    setActionError(null);
    const nextPublic = !speech.is_public;
    try {
      await toggleSpeechVisibility(speechId, nextPublic);
      setSpeech(prev => ({ ...prev, is_public: nextPublic }));
      setPendingPublic(false);
      showToast(nextPublic ? 'Speech is now public' : 'Speech set to private');
    } catch (err) {
      setActionError(err.message || 'Failed to update visibility');
    }
  };

  const handleRegenerate = async () => {
    if (!speech) return;
    setActionError(null);
    setRegenerating(true);
    try {
      const res = await regenerateSpeechFeedback(speechId);
      if (res?.speech) setSpeech(res.speech);
      showToast('Coaching report regenerated');
    } catch (err) {
      setActionError(err.message || 'Failed to regenerate report');
    } finally {
      setRegenerating(false);
    }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', youtube_url: '', synopsis: '', speaker_id: '', tagsInput: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [allMembers, setAllMembers] = useState([]);

  const openEdit = async () => {
    setEditForm({
      title: speech.title || '',
      youtube_url: speech.youtube_url || '',
      synopsis: speech.synopsis || '',
      speaker_id: speech.speaker_id || '',
      tagsInput: Array.isArray(speech.tags) ? speech.tags.join(', ') : (speech.tags || ''),
    });
    // Lazy-load approved members for the speaker dropdown
    if (allMembers.length === 0) {
      try {
        // No status filter: a provisional speaker (pending until they claim
        // their invite) must appear here, or editing a video attributed to one
        // would show an empty Speaker field and silently reassign on save.
        const res = await getMembers();
        if (res?.members) setAllMembers(res.members.filter(m => m.is_active !== false && m.is_active !== 'false'));
      } catch { /* silently fail — dropdown will be empty */ }
    }
    setActionError(null);
    setAudioFile(null);
    setAudioUploadError(null);
    setAudioUploadSuccess(false);
    setEditOpen(true);
  };

  const handleUploadAudio = async () => {
    if (!audioFile) return;
    setAudioUploading(true);
    setAudioUploadPercent(0);
    setAudioUploadError(null);
    setAudioUploadSuccess(false);
    try {
      const fileSize = audioFile.size;
      const duration = await probeDuration(audioFile);
      const res = await uploadSpeechAudio(speechId, audioFile, {
        duration,
        onProgress: (pct) => setAudioUploadPercent(pct),
      });
      setSpeech(prev => ({ ...prev, ...res.speech }));
      if (res.speech?.ai_feedback) setAiFeedback(res.speech.ai_feedback);
      setAudioFile(null);
      setAudioUploadSuccess(true);
      if (res.transcription_status === 'processing') {
        setTranscribingFileSize(fileSize);
      }
    } catch (err) {
      setAudioUploadError(err.message || 'Upload failed');
    } finally {
      setAudioUploading(false);
    }
  };

  const handleAnalyzeUrl = async () => {
    const trimmedUrl = audioUrl.trim();
    if (!trimmedUrl) return;
    setAudioUrlUploading(true);
    setAudioUrlError(null);
    try {
      const res = await analyzeSpeechFromUrl(speechId, trimmedUrl);
      setSpeech(prev => ({ ...prev, ...res.speech }));
      setAudioUrl('');
      // URL flow doesn't know the file size — progress bar will be indeterminate.
      setTranscribingFileSize(0);
    } catch (err) {
      setAudioUrlError(err.message || 'Failed to start analysis');
    } finally {
      setAudioUrlUploading(false);
    }
  };

  const handleClearAudio = async () => {
    setClearingAudio(true);
    try {
      await clearSpeechAudio(speechId);
      // Explicitly null out the cleared fields in local state.
      // Do NOT spread res.speech here — speechService strips null values from
      // the DataStore response, so the spread would silently keep old values.
      setSpeech(prev => ({
        ...prev,
        audio_file_id: null,
        transcript: null,
        metrics: null,
        ai_feedback: null,
      }));
      setAiFeedback(null);
      setAudioFile(null);
      setAudioUploadSuccess(false);
      setTranscribingFileSize(0);
    } catch (err) {
      setActionError(err.message || 'Failed to clear audio analysis');
    } finally {
      setClearingAudio(false);
    }
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    setActionError(null);
    try {
      const tags = editForm.tagsInput.split(',').map(t => t.trim()).filter(Boolean).slice(0, 10);
      const res = await updateSpeech(speechId, {
        speaker_id: editForm.speaker_id || undefined,
        title: editForm.title.trim() || undefined,
        youtube_url: editForm.youtube_url.trim() || null,
        synopsis: editForm.synopsis.trim() || null,
        tags,
      });
      setSpeech(prev => ({ ...prev, ...res.speech }));
      setEditOpen(false);
    } catch (err) {
      setActionError(err.message || 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center py-16 gap-3">
        <Larry mood="thinking" size={64} message="Loading speech..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <Larry mood="sad" size={64} message={error} />
        <Link to="/browse" className="btn-bouncy text-sm px-5 py-2">
          ← Back to Browse
        </Link>
      </div>
    );
  }

  const handleRequestFriend = async (speakerId) => {
    if (!speakerId) return;
    try {
      await requestFriend(speakerId);
      setFriendRequestState('sent');
    } catch (err) {
      // Already asked, or already friends: show the honest state, not an error.
      const already = err.code === 'REQUEST_EXISTS' || err.code === 'ALREADY_FRIENDS';
      setFriendRequestState(already ? 'pending' : 'error');
    }
  };

  if (!speech) return null;

  const isOwner = speech.speaker_id === member?.member_id;
  const isAdmin = member?.role === 'admin';
  const canViewFeedback = !!member;
  const canGiveFeedback = !!member && !isOwner;
  const isPastMeeting = meetingDate ? new Date(meetingDate) < new Date() : true;

  const { title, synopsis, speaker_name, youtube_url, transcript, metrics, is_public, created_at } = speech;
  // Server-side flag. When true the payload has already been stripped of the
  // video, transcript, metrics and feedback — this only decides what we render
  // in their place. Never trust it as the security boundary.
  const locked = speech.locked === true;

  return (
    <>
      <div className="space-y-8 stagger-in">
        {actionError && (
          <div
            className="px-4 py-3 text-sm font-bold rounded-2xl"
            style={{ color: 'var(--color-danger)', background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
          >
            {actionError}
          </div>
        )}

        {/* Transcription in-progress banner — driven by useBackgroundTranscription.
            Renders whenever audio exists but metrics haven't landed yet, including
            after a fresh page load (the hook resumes polling automatically). */}
        {isTranscribing && (() => {
          const minutes = Math.floor(bgElapsed / 60);
          const seconds = bgElapsed % 60;
          const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
          const indeterminate = bgPercent === 0;
          return (
            <>
              {indeterminate && (
                <style>{`
                  @keyframes transcription-indeterminate {
                    0%   { transform: translateX(-100%); }
                    100% { transform: translateX(400%); }
                  }
                `}</style>
              )}
              <div
                className="px-4 py-4 rounded-2xl space-y-3"
                style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)' }}
              >
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: '1.1rem' }}>⏳</span>
                  <p className="text-sm font-bold" style={{ color: 'var(--color-info)' }}>Analysis in progress</p>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Audio is being transcribed and analysed. This usually takes 1–5 minutes depending on file length.
                  The page will update automatically — please keep it open.
                </p>
                <div
                  className="w-full rounded-full overflow-hidden relative"
                  style={{ height: '5px', background: 'var(--accent-dim)' }}
                >
                  {indeterminate ? (
                    <div
                      style={{
                        position: 'absolute', top: 0, left: 0,
                        height: '100%', width: '25%', borderRadius: '9999px',
                        background: 'linear-gradient(90deg, var(--accent), var(--color-info))',
                        animation: 'transcription-indeterminate 1.6s ease-in-out infinite',
                      }}
                    />
                  ) : (
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        width: `${bgPercent}%`,
                        borderRadius: '9999px',
                        background: 'linear-gradient(90deg, var(--accent), var(--color-info))',
                      }}
                    />
                  )}
                </div>
                <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
                  {indeterminate ? `${timeStr} elapsed` : `${bgPercent}% — ${timeStr} elapsed`}
                </p>
              </div>
            </>
          );
        })()}

        {/* Transcription failed banner */}
        {(status === 'failed' || bgError) && !isTranscribing && (
          <div
            className="rounded-2xl px-5 py-4 mb-2"
            style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
          >
            <p className="text-sm font-bold" style={{ color: 'var(--color-danger)' }}>Transcription failed</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
              {bgError || 'Something went wrong transcribing this recording. Please try re-uploading it.'}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <Link
              to="/browse"
              className="btn-bouncy text-sm px-4 py-1.5 mb-3 inline-flex items-center gap-1.5"
            >
              ← Browse
            </Link>
            <h1 className="text-2xl font-extrabold break-words" style={{ color: 'var(--text-primary)' }}>{title}</h1>
            <p className="mt-1 font-medium" style={{ color: 'var(--text-faint)' }}>
              {speaker_name ? `by ${speaker_name} · ` : ''}{new Date(Number(created_at)).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
            </p>
            {synopsis && (
              <p className="mt-2 text-sm leading-relaxed italic max-w-prose" style={{ color: 'var(--text-muted)' }}>
                {synopsis}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap sm:shrink-0">
            {isAdmin && !editOpen && (
              <button
                onClick={openEdit}
                className="btn-bouncy text-sm px-4 py-2"
              >
                ✏️ Edit
              </button>
            )}
            {(isOwner || isAdmin) && (
              <button
                onClick={() => {
                  if (!is_public) {
                    setPendingPublic(true);
                  } else {
                    handleToggleVisibility();
                  }
                }}
                className="btn-bouncy text-sm px-4 py-2"
                style={is_public
                  ? { background: 'linear-gradient(135deg, var(--color-success), #2A9D59)' }
                  : { background: 'var(--accent-dim)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)' }
                }
              >
                {is_public ? '👁️ Public' : '🔒 Private'}
              </button>
            )}
          </div>
        </div>

        {/* Visibility confirmation bar */}
        {pendingPublic && (
          <div
            className="px-4 py-3 rounded-2xl text-sm font-medium flex items-center gap-4 flex-wrap"
            style={{ background: 'var(--color-warning-dim)', border: '1px solid var(--color-warning-border)', color: 'var(--text-secondary)' }}
          >
            <span>Everyone in the club will be able to see this.</span>
            <div className="flex gap-3 ml-auto">
              <button
                onClick={() => setPendingPublic(false)}
                className="text-sm font-bold transition-colors"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              >
                Cancel
              </button>
              <button
                onClick={handleToggleVisibility}
                className="btn-bouncy text-sm px-4 py-1.5"
                style={{ background: 'linear-gradient(135deg, var(--color-success), #2A9D59)' }}
              >
                Make it public
              </button>
            </div>
          </div>
        )}

        {/* Inline edit form (admin) */}
        {editOpen && (
          <div
            className="rounded-2xl p-5 space-y-4"
            style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-accent)' }}
          >
            <p className="text-sm font-bold" style={{ color: 'var(--accent)' }}>Edit Speech</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Speaker</label>
                <MemberPicker
                  members={allMembers}
                  value={editForm.speaker_id}
                  onChange={(id) => setEditForm(f => ({ ...f, speaker_id: id }))}
                  placeholder="— select member —"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)' }}
                  onFocus={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Synopsis</label>
                <textarea
                  value={editForm.synopsis}
                  onChange={(e) => setEditForm(f => ({ ...f, synopsis: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-none"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)' }}
                  onFocus={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                  Tags <span className="font-normal" style={{ color: 'var(--text-faint)' }}>(comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={editForm.tagsInput}
                  onChange={(e) => setEditForm(f => ({ ...f, tagsInput: e.target.value }))}
                  placeholder="Leadership, Storytelling, Technical"
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)' }}
                  onFocus={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                />
              </div>
              {isPastMeeting && (
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>YouTube URL (paste any format)</label>
                  <input
                    type="url"
                    value={editForm.youtube_url}
                    onChange={(e) => setEditForm(f => ({ ...f, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=… or youtu.be/…"
                    className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)' }}
                    onFocus={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="btn-bouncy text-sm px-5 py-2 disabled:opacity-50"
              >
                {editSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setEditOpen(false)}
                className="text-sm font-medium transition-colors"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              >
                Cancel
              </button>
            </div>

            {/* Audio analysis upload — hidden only for future meetings */}
            {isPastMeeting && (
              <div
                className="rounded-2xl p-4 space-y-3"
                style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-accent)' }}
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold" style={{ color: 'var(--accent)' }}>Add audio for analysis</p>
                  {speech.audio_file_id && (
                    <button
                      onClick={() => setConfirmClearAudio(true)}
                      disabled={clearingAudio || audioUploading}
                      className="text-xs disabled:opacity-50 transition-opacity hover:opacity-70"
                      style={{ color: 'var(--color-danger)' }}
                    >
                      {clearingAudio ? 'Clearing…' : '🗑 Clear analysis'}
                    </button>
                  )}
                </div>
                <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
                  Transcribes the speech, runs metrics, generates AI coaching. Replaces whatever's there now.
                </p>
                <input
                  type="file"
                  accept="audio/*,video/mp4,video/webm"
                  onChange={(e) => {
                    setAudioFile(e.target.files[0] || null);
                    setAudioUploadError(null);
                    setAudioUploadSuccess(false);
                  }}
                  className="block w-full text-sm cursor-pointer"
                  style={{ color: 'var(--text-muted)' }}
                />
                {audioFile && (
                  <p className="text-xs" style={{ color: 'var(--text-faint)' }}>{audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(1)} MB)</p>
                )}
                {audioUploadError && (
                  <p className="text-xs" style={{ color: 'var(--color-danger)' }}>{audioUploadError}</p>
                )}
                {audioUploadSuccess && !isTranscribing && (
                  <p className="text-xs" style={{ color: 'var(--color-success)' }}>✓ Done — transcript and metrics updated</p>
                )}
                {isTranscribing && (
                  <p className="text-xs" style={{ color: 'var(--color-info)' }}>
                    ⏳ Previous upload is still being processed — uploading again will replace it.
                  </p>
                )}
                <button
                  onClick={handleUploadAudio}
                  disabled={!audioFile || audioUploading}
                  className="btn-bouncy text-sm px-5 py-2 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #6B67FF, var(--color-info))' }}
                >
                  {audioUploading ? `Uploading… ${audioUploadPercent}%` : 'Upload and analyse'}
                </button>
                {audioUploading && (
                  <div
                    className="w-full rounded-full overflow-hidden"
                    style={{ height: '4px', background: 'var(--accent-dim)' }}
                  >
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${audioUploadPercent}%`,
                        borderRadius: '9999px',
                        background: 'linear-gradient(90deg, var(--accent), var(--color-info))',
                      }}
                    />
                  </div>
                )}

                <p className="text-xs font-bold mt-3" style={{ color: 'var(--text-muted)' }}>Or paste an audio URL</p>
                <p className="text-xs" style={{ color: 'var(--text-faint)' }}>Zoho WorkDrive external share links are supported.</p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={audioUrl}
                    onChange={(e) => { setAudioUrl(e.target.value); setAudioUrlError(null); }}
                    placeholder="https://workdrive.zohoexternal.in/external/…/download"
                    className="flex-1 px-3 py-2 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)' }}
                    onFocus={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                  />
                  <button
                    onClick={handleAnalyzeUrl}
                    disabled={!audioUrl.trim() || audioUrlUploading}
                    className="btn-bouncy text-sm px-4 py-2 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, var(--accent), var(--color-info))', whiteSpace: 'nowrap' }}
                  >
                    {audioUrlUploading ? 'Fetching…' : 'Analyse URL'}
                  </button>
                </div>
                {audioUrlError && (
                  <p className="text-xs" style={{ color: 'var(--color-danger)' }}>{audioUrlError}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Video — or the lock panel in its place. */}
        {locked ? (
          <div
            className="rounded-2xl px-6 py-10 flex flex-col items-center text-center gap-3"
            style={{ background: 'var(--bg-surface)', border: '1px dashed var(--border-subtle)' }}
          >
            <span className="text-4xl" style={{ opacity: 0.35 }} aria-hidden="true">🔒</span>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              This video is friends-only
            </p>
            <p className="text-sm max-w-sm" style={{ color: 'var(--text-muted)' }}>
              {speaker_name
                ? `Become friends with ${speaker_name} to watch their videos.`
                : 'Become friends with the speaker to watch their videos.'}
            </p>
            <LockedSpeechCTA
              speakerId={speech.speaker_id}
              speakerName={speaker_name}
              isProvisional={speech.speaker_provisional === true}
              state={friendRequestState}
              onRequest={handleRequestFriend}
            />
          </div>
        ) : (
          speech.has_media
            ? <UploadedVideoPlayer speechId={speech.speech_id} title={title} hasPoster={speech.has_video === true} />
            : <YouTubeEmbed url={youtube_url} title={title} />
        )}

        {/* Coaching report — one consolidated, ordered report:
            the coaching leads (the actionable part), then full metrics &
            structure and the transcript are collapsed below so the page isn't
            a wall of overlapping analyses. Permission guards are preserved per
            block: metrics show whenever present; transcript + AI coaching
            require canViewFeedback. */}
        {!locked && (metrics || (aiFeedback && canViewFeedback)) && (
          <section id="ai-feedback">
            <div className="flex items-center justify-between gap-2.5 mb-4">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-0.5 h-3 rounded-full flex-shrink-0"
                  style={{ background: 'linear-gradient(180deg, var(--accent) 0%, rgba(45,111,224,0) 100%)' }}
                />
                <h2 className="font-mono text-[10px] font-medium uppercase tracking-[0.2em]"
                    style={{ color: 'var(--text-faint)' }}>
                  Coaching report
                </h2>
              </div>
              {isAdmin && transcript && (
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="text-xs px-3 py-1.5 rounded-lg disabled:opacity-50 transition-opacity hover:opacity-80"
                  style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-accent)', color: 'var(--accent)' }}
                >
                  {regenerating ? 'Regenerating…' : (metrics?.content || metrics?.hedging) ? '↻ Regenerate report' : '✨ Upgrade report'}
                </button>
              )}
            </div>

            <div className="space-y-5">
              {/* 1. The coaching — leads, always open */}
              {aiFeedback && canViewFeedback && <CoachingReport aiFeedback={aiFeedback} />}

              {/* 2. Full metrics & structure — collapsed when coaching is shown
                  above it; open when it's the primary content. */}
              {metrics && (
                <Collapsible
                  label="Metrics & structure"
                  defaultOpen={!(aiFeedback && canViewFeedback)}
                >
                  <SpeechReport metrics={metrics} transcript={null} showTranscript={false} />
                </Collapsible>
              )}

              {/* 3. Transcript — collapsed, at the bottom (reference, not lead) */}
              {transcript && canViewFeedback && (
                <Collapsible label="Transcript">
                  <div
                    className="rounded-2xl p-5 max-h-96 overflow-y-auto"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
                  >
                    <p className="text-sm whitespace-pre-wrap leading-relaxed font-medium" style={{ color: 'var(--text-muted)' }}>
                      {transcript}
                    </p>
                  </div>
                </Collapsible>
              )}
            </div>
          </section>
        )}

        {/* Evaluator Feedback */}
        {canViewFeedback && (
          <section id="feedback">
            <h2 className="text-lg font-extrabold mb-4" style={{ color: 'var(--text-primary)' }}>
              From the room
              {feedback.length > 0 && (
                <span className="ml-2 text-sm font-bold" style={{ color: 'var(--text-faint)' }}>({feedback.length})</span>
              )}
            </h2>

            {feedback.length === 0 ? (
              <div className="card-paper p-6 flex flex-col items-center text-center gap-2">
                <Larry mood="happy" size={48} message={isOwner ? 'Your feedback is coming.' : 'No feedback yet.'} />
              </div>
            ) : (
              <div className="space-y-4">
                {feedback.map((fb) => (
                  <FeedbackItem
                    key={fb.feedback_id}
                    feedback={fb}
                    isAuthor={fb.author_id === member?.member_id}
                    onEdit={handleEditFeedback}
                    onDelete={handleDeleteFeedback}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Comment form — available to all approved members except the speaker */}
        {canGiveFeedback && (
          <section>
            <h2 className="text-lg font-extrabold mb-4" style={{ color: 'var(--text-primary)' }}>Leave a comment</h2>
            <div className="card-paper p-5">
              <FeedbackForm key={feedback.length} onSubmit={handleSubmitFeedback} />
            </div>
          </section>
        )}
      </div>

      {confirmClearAudio && (
        <ConfirmDialog
          message="Remove the transcript, metrics, and AI feedback? The speech record and member comments will be kept."
          confirmLabel="Clear analysis"
          variant="danger"
          onConfirm={() => { setConfirmClearAudio(false); handleClearAudio(); }}
          onCancel={() => setConfirmClearAudio(false)}
        />
      )}
    </>
  );
}


